import type { Feature, FeatureCollection, Point, Polygon } from 'geojson';

export const TRACES_GEO_SOURCE_POINTS = 'traces-geo-points-src';
export const TRACES_GEO_SOURCE_POLYGONS = 'traces-geo-polygons-src';
export const TRACES_GEO_LAYER_POINTS = 'traces-geo-points';
export const TRACES_GEO_LAYER_LABELS = `${TRACES_GEO_LAYER_POINTS}-label`;
export const TRACES_GEO_LAYER_POLYGONS = 'traces-geo-polygons';
export const TRACES_GEO_LAYER_POLYGON_CASING = `${TRACES_GEO_LAYER_POLYGONS}-casing`;
export const TRACES_GEO_COLOR = '#d500f9';
const TRACES_LAYERS_BOTTOM_UP = [
  TRACES_GEO_LAYER_POLYGON_CASING,
  TRACES_GEO_LAYER_POLYGONS,
  TRACES_GEO_LAYER_POINTS,
  TRACES_GEO_LAYER_LABELS,
];

export interface TracesGeometryLayers {
  points: FeatureCollection<Point>;
  polygons: FeatureCollection<Polygon>;
}

export interface TracesLayerMap {
  getStyle?(): { glyphs?: string; layers?: { id: string }[] } | undefined;
  getSource(id: string): { setData(data: FeatureCollection): void } | undefined;
  addSource(id: string, source: object): unknown;
  getLayer(id: string): unknown;
  addLayer(layer: object): unknown;
  moveLayer(id: string): unknown;
  on(type: 'styledata', listener: () => void): unknown;
}

const emptyCollection = <G extends Point | Polygon>(): FeatureCollection<G> => ({
  type: 'FeatureCollection',
  features: [],
});

const areaLabel = (hectares: unknown): string =>
  typeof hectares === 'number' && Number.isFinite(hectares) ? `${hectares.toFixed(2)} ha` : '';

export function tracesGeometryLayer(traces: FeatureCollection | undefined): TracesGeometryLayers {
  const layers: TracesGeometryLayers = { points: emptyCollection<Point>(), polygons: emptyCollection<Polygon>() };
  for (const feature of traces?.features ?? []) {
    if (feature.geometry?.type === 'Point' && feature.properties?.['shape'] === 'point') continue;
    if (feature.geometry?.type === 'Point') {
      const properties = { ...feature.properties, label: areaLabel(feature.properties?.['Area']) };
      layers.points.features.push({ ...feature, properties, geometry: feature.geometry } as Feature<Point>);
    } else if (feature.geometry?.type === 'Polygon') {
      layers.polygons.features.push({ ...feature, geometry: feature.geometry } as Feature<Polygon>);
    }
  }
  return layers;
}

export function sourceIndexOf(traces: FeatureCollection | undefined, derivedIndex: number): number {
  const sourceFeature = traces?.features?.[derivedIndex]?.properties?.['sourceFeature'];
  return typeof sourceFeature === 'number' ? sourceFeature : derivedIndex;
}

export class TracesGeometryLayerController {
  private layers: TracesGeometryLayers = tracesGeometryLayer(undefined);

  constructor(private readonly map: TracesLayerMap) {}

  setData(traces: FeatureCollection | undefined): void {
    this.layers = tracesGeometryLayer(traces);
    this.pushData();
  }

  show(): void {
    this.ensureSource(TRACES_GEO_SOURCE_POINTS);
    this.ensureSource(TRACES_GEO_SOURCE_POLYGONS);
    this.ensureLayer({
      id: TRACES_GEO_LAYER_POLYGON_CASING,
      type: 'line',
      source: TRACES_GEO_SOURCE_POLYGONS,
      paint: { 'line-color': '#fff', 'line-width': 7, 'line-opacity': 0.9 },
    });
    this.ensureLayer({
      id: TRACES_GEO_LAYER_POLYGONS,
      type: 'line',
      source: TRACES_GEO_SOURCE_POLYGONS,
      paint: { 'line-color': TRACES_GEO_COLOR, 'line-width': 4, 'line-dasharray': [2, 1] },
    });
    this.ensureLayer({
      id: TRACES_GEO_LAYER_POINTS,
      type: 'circle',
      source: TRACES_GEO_SOURCE_POINTS,
      paint: {
        'circle-radius': 7,
        'circle-color': TRACES_GEO_COLOR,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#fff',
      },
    });
    if (this.styleRendersText()) {
      this.ensureLayer({
        id: TRACES_GEO_LAYER_LABELS,
        type: 'symbol',
        source: TRACES_GEO_SOURCE_POINTS,
        layout: { 'text-field': ['get', 'label'], 'text-size': 13, 'text-offset': [0, 1.2], 'text-anchor': 'top' },
        paint: { 'text-color': TRACES_GEO_COLOR, 'text-halo-color': '#fff', 'text-halo-width': 2 },
      });
    }
    this.pushData();
    this.keepOnTop();
    this.map.on('styledata', this.keepOnTop);
  }

  private raising = false;

  private readonly keepOnTop = (): void => {
    if (this.raising) return;
    const own = TRACES_LAYERS_BOTTOM_UP.filter((id) => this.map.getLayer(id));
    const topmost = this.styleLayerIds().slice(-own.length);
    if (own.every((id, index) => topmost[index] === id)) return;
    this.raising = true;
    try {
      own.forEach((id) => this.map.moveLayer(id));
    } finally {
      this.raising = false;
    }
  };

  private styleLayerIds(): string[] {
    try {
      return (this.map.getStyle?.()?.layers ?? []).map((layer) => layer.id);
    } catch {
      return [];
    }
  }

  private styleRendersText(): boolean {
    try {
      return !!this.map.getStyle?.()?.glyphs;
    } catch {
      return false;
    }
  }

  private ensureSource(id: string): void {
    if (!this.map.getSource(id)) this.map.addSource(id, { type: 'geojson', data: emptyCollection() });
  }

  private ensureLayer(layer: { id: string; [property: string]: unknown }): void {
    if (!this.map.getLayer(layer.id)) this.map.addLayer(layer);
  }

  private pushData(): void {
    this.map.getSource(TRACES_GEO_SOURCE_POINTS)?.setData(this.layers.points);
    this.map.getSource(TRACES_GEO_SOURCE_POLYGONS)?.setData(this.layers.polygons);
  }
}
